import { Result, handleServerErrors, Events, CustomError, ServerError } from 'core/infrastructure';
import {
	TicketDTO,
	SaleDTO,
	PaymentDTO,
	CanReopenDeliveryNoteResultDTO,
	TicketLocationDTO,
	BuyerCustomerDTO,
	InvoiceParamsFromPosDTO,
} from 'core/api';
import { DeliveryNoteFacade, PrintFacade } from 'pos/facades';

export async function addPayments(deliveryNoteId: number, payments: PaymentDTO[], prepayAmount: number): Promise<Result<SaleDTO>> {
	return await run(async () => {
		const sale = await DeliveryNoteFacade.addPayments(deliveryNoteId, payments, prepayAmount);
		await Events.raise('pos:delivery-note:changed');
		return sale;
	});
}

export async function canBeReopened(deliveryNoteId: number): Promise<Result<CanReopenDeliveryNoteResultDTO>> {
	return await run(async () => DeliveryNoteFacade.canBeReopened(deliveryNoteId));
}

export async function reopen(deliveryNoteId: number, location: TicketLocationDTO): Promise<Result<TicketDTO>> {
	return await run(async () => DeliveryNoteFacade.reopen(deliveryNoteId, location));
}

export async function reopenInTicket(deliveryNoteId: number, location: TicketLocationDTO, targetTicketId: number): Promise<Result<TicketDTO>> {
	return await run(async () => DeliveryNoteFacade.reopenInTicket(deliveryNoteId, location, targetTicketId));
}

export async function changeCustomer(deliveryNoteId: number, customer: BuyerCustomerDTO): Promise<void> {
	await run(async () => {
		await DeliveryNoteFacade.changeCustomer(deliveryNoteId, customer);
		await Events.raise('pos:delivery-note:changed');
	});
}

export async function printDeliveryNotesProform(deliveryNoteIds: number[], params: PrintFacade.PrintParams): Promise<void> {
	await run(async () => await PrintFacade.printDeliveryNotesOnPrinter(deliveryNoteIds, params));
}

export async function printDeliveryNote(deliveryNoteId: number, params: PrintFacade.PrintParams): Promise<void> {
	await run(async () => await PrintFacade.printDeliveryNoteOnPrinter(deliveryNoteId, params));
}

export async function createRefund(deliveryNoteIds: number[], params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return run(async () => {
		const sale = await DeliveryNoteFacade.createRefund(deliveryNoteIds, params);
		await Events.raise('pos:delivery-note:changed');
		return sale;
	});
}

export async function createInvoice(deliveryNoteIds: number[], params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return run(async () => {
		const sale = await DeliveryNoteFacade.createInvoice(deliveryNoteIds, params);
		await Events.raise('pos:delivery-note:changed');
		return sale;
	});
}

export async function cancel(deliveryNoteIds: number[], printCancellations: boolean): Promise<void> {
	await run(async () => {
		await DeliveryNoteFacade.cancel(deliveryNoteIds, printCancellations);
		await Events.raise('pos:delivery-note:changed');
	});
}

export async function setTableCompanions(deliveryNoteId: number, tableCompanions: Nullable<number>) {
	await run(async () => {
		await DeliveryNoteFacade.setTableCompanions(deliveryNoteId, tableCompanions);
		await Events.raise('pos:delivery-note:changed');
	});
}


async function run<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {
	const handledError: CustomError = new ServerError('ERROR NO CAPTURADO');

	const result = await handleServerErrors(fn, {
		onDomainError: async (domainError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: domainError.message, error: domainError });
		},

		onPrintError: async (printError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: printError.message, error: printError });
		},

		onInvalidPaymentError: async (paymentError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: paymentError.message, error: paymentError });
		},

		onFrozenDeliveryNoteError: async (frozenDeliveryNoteError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: frozenDeliveryNoteError.message, error: frozenDeliveryNoteError });
		},

		onFrozenTicketError: async (frozenTicketError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: frozenTicketError.message, error: frozenTicketError });
		},

		onInvalidCustomerCifError: async (invalidCustomerError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: invalidCustomerError.message, error: invalidCustomerError });
		},

		onDeliveryNoteNotFoundError: async (deliveryNoteNotFoundError) => {
			await Events.raise('pos:delivery-note:operation-failed', { msg: deliveryNoteNotFoundError.message, error: deliveryNoteNotFoundError });
		}
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}
